import { digitCounts } from '@zonofi/i18n';
import { TreeFilterOption, TreeInputData } from '@zonofi/zono-money-store';

interface FormValidationParams {
  formData: Record<string, string>;
  requiredFields: string[];
  selectedCountryCode: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const isValidEmail = (email?: string) => {
  if (!email) return true;
  return EMAIL_REGEX.test(email.trim());
};

export const validateAddCustomerForm = ({
  formData,
  requiredFields,
  selectedCountryCode,
}: FormValidationParams): boolean => {
  const hasRequiredFields = requiredFields.every(
    (key) => formData[key]?.toString().trim().length > 0
  );
  if (!hasRequiredFields) return false;

  const expectedMobileLength = digitCounts[selectedCountryCode] || 10;
  const isValidMobile = formData.mobileNumber?.length === expectedMobileLength;
  if (!isValidMobile) return false;

  const isEmailValid = isValidEmail(formData.emailId);
  if (!isEmailValid) return false;

  return true;
};

export const initializeTreeData = (
  treeData: TreeFilterOption | undefined,
  assignedRouteIds: string[],
  selectedRouteId: string[]
): TreeFilterOption | undefined => {
  if (!treeData?.value) return treeData;

  const updateValue = (value: TreeInputData[]): TreeInputData[] =>
    value.map((tree) => {
      const hasChildren = !!tree.data?.length;
      const isAssigned = assignedRouteIds?.includes(tree.id);
      const localAssigned = selectedRouteId?.includes(tree.id);

      return {
        ...tree,
        selected: hasChildren || isAssigned || localAssigned,
        ...(isAssigned && { isDisable: true }),
        data: tree.data ? updateValue(tree.data) : undefined,
      };
    });

  return {
    ...treeData,
    value: updateValue(treeData.value),
  };
};

export const searchTreeDataByLabel = (
  tree: TreeInputData[],
  searchKey: string
): TreeInputData[] => {
  const keyword = searchKey.toLowerCase();
  return tree
    .map((value) => {
      const labelMatches = value.label?.toLowerCase().includes(keyword);
      const hasChildren = !!value.data?.length;
      if (!hasChildren) {
        return labelMatches ? value : null;
      }
      const filteredChildren = searchTreeDataByLabel(value.data!, searchKey);

      if (labelMatches) {
        return {
          ...value,
          selected: true,
          data: value.data,
        };
      }

      if (filteredChildren.length > 0) {
        return {
          ...value,
          selected: true,
          data: filteredChildren,
        };
      }
      return null;
    })
    .filter(Boolean) as TreeInputData[];
};
